import {
  Paper,
  Box,
  Typography,
  Grid,
  Button,
  Tooltip,
  Autocomplete,
  TextField,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import * as React from "react";
import { getEventMatches } from "../api/event";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { serverSideMatches } from "../api/matches";
import { scheduleCols } from "../columns/scheduleColumns";
import { makeScheduleCols } from "../columns/makeScheduleColumns";
import {
  getSchedule,
  createScheduleRow,
  editScheduleRow,
} from "../api/schedule";
import { useGlobal } from "../context/GlobalProvider";
import { getTeamsUsers } from "../api/user";

async function GetEventMatches(event) {
  try {
    const data = await getEventMatches(event, "");
    return data;
  } catch (error) {
    console.error("Error fetching matches:", error);
    return { body: JSON.stringify({ data: [] }) };
  }
}

async function GetSchedule(team, event, user_id) {
  const data = await getSchedule({
    team: team,
    event: event,
    user_id: user_id,
  });
  return data;
}

const fetchUsers = async () => {
  try {
    const result = await getTeamsUsers(5804);
    return JSON.parse(result?.body)?.data || [];
  } catch (err) {
    console.error("Failed to fetch users", err);
    return [];
  }
};

export function MentorSchedule({
  user,
  scheduleData,
  numMatches,
  setPendingChanges,
}) {
  const handleAssignmentChange = (change) => {
    setPendingChanges((prev) => {
      const existingIndex = prev.findIndex(
        (c) =>
          c.match_number === change.match_number &&
          c.robot_slot === change.robot_slot
      );
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = change;
        return updated;
      }
      return [...prev, change];
    });
  };

  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    fetchUsers().then(setUsers);
  }, []);

  useEffect(() => {
    setRows(
      Array.from({ length: numMatches }, (_, i) => ({
        match_number: i + 1,
      }))
    );
  }, [numMatches]);

  const columns = useMemo(
    () => makeScheduleCols(scheduleData, users, handleAssignmentChange),
    [scheduleData, users, handleAssignmentChange]
  );

  return (
    <DataGridPro
      rows={rows}
      getRowId={(row) => row.match_number}
      columns={columns}
      sx={{ border: "none", p: 1 }}
      disableSelectionOnClick
      editMode="row"
      pagination
      pageSizeOptions={[5, 10, 25]}
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: 10,
          },
        },
        pinnedColumns: { right: ["actions"] },
      }}
    />
  );
}

export function ScoutingSchedule({ user, event }) {
  const [scheduleData, setScheduleData] = useState([]);
  const [matchData, setMatchData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [updateScheduleData, setUpdateScheduleData] = useState(false);
  useEffect(() => {
    if (event != null) {
      setColumns(scheduleCols() || []);
      const fetchScheduleData = async () => {
        const result = await GetSchedule(
          user?.team_number,
          event,
          user?.user_id
        );
        try {
          const parsedData = JSON.parse(result?.body);
          setScheduleData(parsedData?.data);
          setUpdateScheduleData(true);
        } catch (error) {
          setScheduleData([]);
          setUpdateScheduleData(true);
          console.error("Error parsing schedule data:", error);
        }
      };

      fetchScheduleData();
    }
  }, [event]);

  useEffect(() => {
    const fetchMatchData = async () => {
      if (scheduleData.length === 0) return;
      const result = await GetEventMatches(event);
      try {
        const sortedData = result.sort((a, b) => a.time - b.time);
        setMatchData(sortedData);

        setScheduleData((prev) => {
          const updatedData = prev.map((row) => {
            const match = sortedData.find(
              (match) => match.match_number === row.match_number
            );

            if (!match) return row;

            const isRed = row.robot_slot.includes("red");
            const alliance = isRed ? "red" : "blue";
            const index =
              parseInt(
                row.robot_slot.replace("red", "").replace("blue", ""),
                10
              ) - 1;

            return {
              ...row,
              robot: Number(
                match.alliances[alliance].team_keys[index].replace("frc", "")
              ),
              eta:
                match.time * 1000 < Date.now()
                  ? "Done"
                  : new Date(match.time * 1000).toLocaleString(undefined, {
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                      hour: "numeric",
                      minute: "numeric",
                    }),
            };
          });

          return updatedData;
        });
      } catch (error) {
        console.error("Error parsing user data:", error);
        setMatchData([]);
      }
    };

    fetchMatchData();
  }, [updateScheduleData]);

  return (
    <DataGridPro
      rows={scheduleData}
      getRowId={(row) => row.schedule_id}
      columns={columns}
      sx={{ display: "flex", minHeight: "125px" }}
    />
  );
}
